import React from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, Easing } from "remotion";

export type BugType = "lower_third" | "score_bug" | "pick_card";
export type BugPosition = "bottom_left" | "bottom_right" | "bottom_center" | "top_left" | "top_right";

export interface SportsStatsBugProps {
  type: BugType;
  // lower_third
  playerName?: string;
  statLine?: string;
  teamColor?: string;
  teamName?: string;
  jersey?: string;
  // score_bug
  homeTeam?: string;
  homeScore?: number;
  awayTeam?: string;
  awayScore?: number;
  homeAbbr?: string;
  awayAbbr?: string;
  period?: string;
  clock?: string;
  // pick_card
  pick?: string;
  odds?: string;
  confidence?: number;
  record?: string;
  pickLabel?: string;
  sport?: string;
  // shared
  accentColor?: string;
  brandName?: string;
  position?: BugPosition;
  totalFrames?: number;
}

const CHROMA = "#FF00FF";
const DEFAULT_ACCENT = "#38bdf8";
const FONT_STACK = "'Arial Black', 'Arial', 'Impact', sans-serif";
const FONT_BODY  = "'Arial', sans-serif";

function slideProgress(frame: number, total: number, inF = 18, outF = 18): number {
  return interpolate(
    frame,
    [0, inF, total - outF, total],
    [0, 1, 1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp",
      easing: Easing.out(Easing.cubic) }
  );
}

// ── LOWER THIRD ───────────────────────────────────────────────────────────────
const LowerThird: React.FC<{ p: SportsStatsBugProps; prog: number }> = ({ p, prog }) => {
  const accent = p.teamColor || p.accentColor || DEFAULT_ACCENT;
  const ty = interpolate(prog, [0, 1], [110, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const op = interpolate(prog, [0, 0.25], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ background: CHROMA, justifyContent: "flex-end" }}>
      <div style={{ transform: `translateY(${ty}px)`, opacity: op, width: "100%", paddingBottom: 72 }}>
        <div style={{ display: "flex", alignItems: "stretch", background: "rgba(6,8,16,0.96)", borderTop: `3px solid ${accent}`, boxShadow: "0 -6px 40px rgba(0,0,0,0.65)" }}>
          {/* Accent stripe */}
          <div style={{ width: 10, background: accent, flexShrink: 0, boxShadow: `0 0 24px ${accent}99` }} />
          {/* Text */}
          <div style={{ flex: 1, padding: "16px 28px 14px 20px" }}>
            <div style={{ fontSize: 54, fontWeight: 900, color: "#FFFFFF", fontFamily: FONT_STACK, letterSpacing: -0.5, lineHeight: 1.0, textTransform: "uppercase" }}>
              {p.playerName || "PLAYER NAME"}
            </div>
            {p.teamName && (
              <div style={{ fontSize: 24, fontWeight: 700, color: accent, fontFamily: FONT_BODY, letterSpacing: 2.5, textTransform: "uppercase", marginTop: 3, marginBottom: 4 }}>
                {p.teamName}{p.jersey ? `  •  #${p.jersey}` : ""}
              </div>
            )}
            <div style={{ fontSize: 30, fontWeight: 600, color: "rgba(255,255,255,0.80)", fontFamily: FONT_BODY, marginTop: p.teamName ? 0 : 5, letterSpacing: 0.3 }}>
              {p.statLine || ""}
            </div>
          </div>
          {/* Brand */}
          <div style={{ display: "flex", alignItems: "flex-end", paddingRight: 22, paddingBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: accent, fontFamily: FONT_STACK, letterSpacing: 2, opacity: 0.82, textTransform: "uppercase" }}>
              {p.brandName || "POWERPICKS"}
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ── SCORE BUG ─────────────────────────────────────────────────────────────────
const ScoreBug: React.FC<{ p: SportsStatsBugProps; prog: number }> = ({ p, prog }) => {
  const accent = p.accentColor || DEFAULT_ACCENT;
  const ty = interpolate(prog, [0, 1], [80, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const op = interpolate(prog, [0, 0.35], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const isRight = !p.position || p.position === "bottom_right";

  return (
    <AbsoluteFill style={{ background: CHROMA, justifyContent: "flex-end", alignItems: isRight ? "flex-end" : "flex-start" }}>
      <div style={{ transform: `translateY(${ty}px)`, opacity: op, paddingBottom: 72, paddingRight: isRight ? 36 : 0, paddingLeft: isRight ? 0 : 36 }}>
        <div style={{ background: "rgba(6,8,16,0.97)", border: "1px solid rgba(255,255,255,0.12)", borderBottom: `3px solid ${accent}`, borderRadius: 8, overflow: "hidden", minWidth: 270, boxShadow: "0 8px 36px rgba(0,0,0,0.75)" }}>
          {/* Header */}
          <div style={{ background: accent, padding: "5px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 19, fontWeight: 900, color: "#000", letterSpacing: 1, fontFamily: FONT_STACK }}>{p.period || "LIVE"}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#000", fontFamily: FONT_BODY }}>{p.clock || ""}</div>
          </div>
          {/* Away */}
          <div style={{ padding: "10px 16px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: "rgba(255,255,255,0.82)", fontFamily: FONT_STACK, letterSpacing: 1 }}>
              {p.awayAbbr || (p.awayTeam || "AWY").slice(0, 3).toUpperCase()}
            </div>
            <div style={{ fontSize: 38, fontWeight: 900, color: "rgba(255,255,255,0.82)", fontFamily: FONT_STACK }}>
              {p.awayScore ?? 0}
            </div>
          </div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.08)", marginLeft: 16, marginRight: 16 }} />
          {/* Home */}
          <div style={{ padding: "8px 16px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: accent, fontFamily: FONT_STACK, letterSpacing: 1 }}>
              {p.homeAbbr || (p.homeTeam || "HME").slice(0, 3).toUpperCase()}
            </div>
            <div style={{ fontSize: 38, fontWeight: 900, color: accent, fontFamily: FONT_STACK }}>
              {p.homeScore ?? 0}
            </div>
          </div>
          {/* Footer brand */}
          <div style={{ background: "rgba(0,0,0,0.45)", padding: "3px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", letterSpacing: 2, fontFamily: FONT_BODY, textTransform: "uppercase" }}>
              {p.brandName || "POWERPICKS"}
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ── PICK CARD ─────────────────────────────────────────────────────────────────
const PickCard: React.FC<{ p: SportsStatsBugProps; prog: number; frame: number }> = ({ p, prog, frame }) => {
  const accent = p.accentColor || DEFAULT_ACCENT;
  const ty = interpolate(prog, [0, 1], [100, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const op = interpolate(prog, [0, 0.25], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const conf = p.confidence ?? 75;
  const confW = interpolate(frame, [18, 55], [0, conf], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ background: CHROMA, justifyContent: "flex-end", alignItems: "center" }}>
      <div style={{ transform: `translateY(${ty}px)`, opacity: op, width: "100%", paddingBottom: 80, paddingLeft: 36, paddingRight: 36 }}>
        <div style={{ background: "linear-gradient(135deg,rgba(8,10,18,0.97) 0%,rgba(12,18,30,0.97) 100%)", border: `1px solid ${accent}33`, borderTop: `3px solid ${accent}`, borderRadius: 12, padding: "20px 24px 18px", boxShadow: `0 8px 44px rgba(0,0,0,0.72),0 0 60px ${accent}18` }}>
          {/* Label row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ background: accent, color: "#000", fontSize: 20, fontWeight: 900, letterSpacing: 2, padding: "4px 14px", borderRadius: 4, fontFamily: FONT_STACK, textTransform: "uppercase" }}>
              {p.pickLabel || "BEST BET"}
            </div>
            {p.sport && (
              <div style={{ fontSize: 18, color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY, letterSpacing: 1 }}>
                {p.sport.toUpperCase()}
              </div>
            )}
          </div>
          {/* Pick */}
          <div style={{ fontSize: 50, fontWeight: 900, color: "#FFF", fontFamily: FONT_STACK, lineHeight: 1.1, marginBottom: 4 }}>
            {p.pick || "PICK TBD"}
          </div>
          {/* Odds */}
          <div style={{ fontSize: 28, color: accent, fontWeight: 700, fontFamily: FONT_BODY, marginBottom: 14 }}>
            {p.odds || "EVEN"}
          </div>
          {/* Confidence bar */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
              <div style={{ fontSize: 17, color: "rgba(255,255,255,0.45)", fontFamily: FONT_BODY, letterSpacing: 1 }}>CONFIDENCE</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: accent, fontFamily: FONT_STACK }}>{conf}%</div>
            </div>
            <div style={{ height: 8, background: "rgba(255,255,255,0.1)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${confW}%`, background: `linear-gradient(90deg,${accent}88,${accent})`, borderRadius: 4 }} />
            </div>
          </div>
          {/* Footer */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 11 }}>
            {p.record && (
              <div style={{ fontSize: 19, color: "rgba(255,255,255,0.55)", fontFamily: FONT_BODY }}>
                Record: <span style={{ color: "#FFF", fontWeight: 700 }}>{p.record}</span>
              </div>
            )}
            <div style={{ fontSize: 17, fontWeight: 900, color: accent, fontFamily: FONT_STACK, letterSpacing: 2, marginLeft: "auto" }}>
              {p.brandName || "POWERPICKS.AI"}
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ── ROOT EXPORT ───────────────────────────────────────────────────────────────
export const SportsStatsBug: React.FC<SportsStatsBugProps> = (props) => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();
  const prog = slideProgress(frame, durationInFrames);

  if (props.type === "lower_third") return <LowerThird p={props} prog={prog} />;
  if (props.type === "score_bug")   return <ScoreBug   p={props} prog={prog} />;
  return <PickCard p={props} prog={prog} frame={frame} />;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
